/**
 * Схема человека: какие группы мышц программа грузит мало, нормально и много.
 *
 * Клиент здесь ничего не считает. Зону каждой группы, пороги, число подходов
 * строкой, объяснение, легенду и выбор недели решил движок
 * (`engine.muscle_map`): по строке `zone` схема только выбирает цвет. Если в
 * этом файле появится сравнение с 10 и 20 или умножение на 0.5 — это второй
 * источник правды, и он разойдётся с флагами движка.
 *
 * ЦВЕТ НЕ ЕДИНСТВЕННЫЙ НОСИТЕЛЬ СМЫСЛА. Красное — ещё и с полосками, у
 * зелёного своя прозрачность, у каждой группы есть строка со словом и
 * числом, а у фигуры — описание словами для VoiceOver. Области на фигуре
 * мельче пальца, поэтому основной путь — список под схемой; тап по области
 * лишь подсвечивает её строку и показывает объяснение.
 */

import { useId, useState } from 'react'

import type { MuscleGroupLoad, MuscleMap } from '../api/types'
import {
  BODY_REGIONS,
  DETAIL_LINES,
  FIGURE_HEIGHT,
  FIGURE_WIDTH,
  SILHOUETTE,
  type BodyView,
} from './bodyMapShapes'
import { Empty } from './ui'

const KNOWN_ZONES = new Set(['none', 'low', 'corridor', 'high', 'no_norm', 'untracked'])

/** Незнакомую зону (сервер новее клиента) рисуем как «не отслеживается» — без цвета и вердикта. */
function zoneOf(group: MuscleGroupLoad | undefined): string {
  return group && KNOWN_ZONES.has(group.zone) ? group.zone : 'untracked'
}

/** Группы, у которых есть строка в списке: с подходами. */
const LISTED = new Set(['low', 'corridor', 'high', 'no_norm'])

export default function BodyMap({ map, compact = false }: { map: MuscleMap; compact?: boolean }) {
  // Идентификатор без двоеточий: из useId они ломают ссылку url(#…) на
  // узор, а на «Готовых программах» открыто сразу несколько схем.
  const uid = useId().replace(/:/g, '')
  const [selected, setSelected] = useState<string | null>(null)

  if (map.status !== 'ok' || map.groups.length === 0) {
    return <Empty title="Раскраски нет" hint={map.message} />
  }

  const byGroup = new Map(map.groups.map((g) => [g.group, g]))
  const chosen = selected ? byGroup.get(selected) : undefined
  const listed = map.groups.filter((g) => LISTED.has(zoneOf(g)))
  const empty = map.groups.filter((g) => zoneOf(g) === 'none')

  function pickOnFigure(group: string): void {
    setSelected(group)
    // Строки есть не у всех: у группы без подходов её нет, и тогда тап только
    // показывает объяснение над списком.
    document.getElementById(`${uid}-row-${group}`)?.scrollIntoView({ block: 'nearest' })
  }

  const figure = (view: BodyView, dx: number) => (
    <g transform={`translate(${dx},0)`}>
      {SILHOUETTE[view].map((d, i) => (
        <path key={`s${i}`} className="bm-sil" d={d} />
      ))}
      {BODY_REGIONS.filter((r) => r.view === view).map((region, i) => {
        const group = byGroup.get(region.group)
        const zone = zoneOf(group)
        const pattern =
          zone === 'high' ? `url(#${uid}-hi)` : zone === 'no_norm' ? `url(#${uid}-nn)` : undefined
        return (
          <path
            key={`r${i}`}
            d={region.d}
            className={`bm-region bm-${zone}${selected === region.group ? ' on' : ''}`}
            style={pattern ? { fill: pattern } : undefined}
            onClick={zone === 'untracked' ? undefined : () => pickOnFigure(region.group)}
          >
            {group && <title>{`${group.name}: ${group.zone_label}`}</title>}
          </path>
        )
      })}
      <path className="bm-lines" d={DETAIL_LINES[view]} />
      <text className="bm-caption" x={FIGURE_WIDTH / 2} y={FIGURE_HEIGHT + 6} textAnchor="middle">
        {view === 'front' ? 'спереди' : 'сзади'}
      </text>
    </g>
  )

  return (
    <figure className={`bm${compact ? ' compact' : ''}`}>
      <svg
        className="bm-svg"
        viewBox={`22 0 ${FIGURE_WIDTH * 2 - 44} ${FIGURE_HEIGHT + 10}`}
        role="img"
        aria-labelledby={`${uid}-title ${uid}-desc`}
      >
        <title id={`${uid}-title`}>{map.title}</title>
        <desc id={`${uid}-desc`}>{map.summary}</desc>
        <defs>
          <pattern
            id={`${uid}-hi`}
            width="6"
            height="6"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect className="bm-hi-base" width="6" height="6" />
            <rect className="bm-hi-stripe" width="2" height="6" />
          </pattern>
          <pattern
            id={`${uid}-nn`}
            width="5"
            height="5"
            patternUnits="userSpaceOnUse"
            patternTransform="rotate(45)"
          >
            <rect className="bm-nn-base" width="5" height="5" />
            <rect className="bm-nn-stripe" width="1.4" height="5" />
          </pattern>
        </defs>
        {figure('front', 0)}
        {figure('back', FIGURE_WIDTH)}
      </svg>

      <p className="bm-detail muted" aria-live="polite">
        {chosen ? chosen.reason : 'Нажми на мышцу или на строку ниже — покажу, откуда цифра.'}
      </p>

      <figcaption className="bm-legend">
        {map.legend.map((item) => (
          <div key={item.zone} className="bm-legend-item muted">
            <span className={`bm-sw bm-sw-${item.zone}`} aria-hidden="true" />
            <span>{item.label}</span>
          </div>
        ))}
        {!compact && <div className="bm-note muted">{map.note}</div>}
      </figcaption>

      <div className="bm-list">
        {listed.map((g) => {
          const open = selected === g.group
          const zone = zoneOf(g)
          return (
            <div key={g.group}>
              <button
                type="button"
                id={`${uid}-row-${g.group}`}
                className={`bm-row${open ? ' on' : ''}`}
                aria-expanded={open}
                aria-controls={`${uid}-more-${g.group}`}
                onClick={() => setSelected((prev) => (prev === g.group ? null : g.group))}
              >
                <span className={`bm-sw bm-sw-${zone}`} aria-hidden="true" />
                <span className="bm-row-name">
                  {g.name}
                  {g.has_goal && <span className="pill accent">цель</span>}
                </span>
                <span className="tnum">{g.sets_text}</span>
                <span className={`pill bm-pill-${zone}`}>{g.zone_label}</span>
              </button>
              {open && (
                <div id={`${uid}-more-${g.group}`} className="bm-more muted">
                  <div>{g.reason}</div>
                  {g.exercises.map((c) => (
                    <div key={c.exercise_id}>{c.text}</div>
                  ))}
                </div>
              )}
            </div>
          )
        })}

        {empty.length > 0 && (
          <div className="bm-empty muted">
            Нет учтённых подходов: {empty.map((g) => g.name.toLowerCase()).join(', ')}
          </div>
        )}
        {map.excluded.map((item) => (
          <div key={item.exercise_id} className="bm-empty muted">
            {item.reason}
          </div>
        ))}
      </div>
    </figure>
  )
}
